import { api } from 'aws-blocks';
import { useState } from 'react';
import { FiEdit } from 'react-icons/fi';
import UpdateUserModal, { USER_FIELD_VALUES } from '../../Components/UpdateUserModal';
import BkPanel from '../../Components/ui/BkPanel';
import { useAuthContext } from '../../contexts/AuthContext';
import { useSnackBarContext } from '../../contexts/SnackBarContext';
import useDisplayPieceValues from '../../hooks/use-display-piece-values';
import { useNavigate } from 'react-router-dom';

export default function ProfilePreferences() {
  const { userData, queryUserData } = useAuthContext();
  const { displayPieceValues, setDisplayPieceValues } = useDisplayPieceValues();
  const { addSnackBar } = useSnackBarContext();
  const navigate = useNavigate();
  const [showRename, setShowRename] = useState(false);
  const [deletionBusy, setDeletionBusy] = useState(false);
  const nextUpdateNameAt = userData?.nextUpdateNameAt as number | undefined;
  const deletionRequestedAt = userData?.deletionRequestedAt as
    | number
    | undefined;

  return (
    <div className="mx-auto w-full max-w-3xl space-y-4 px-4 py-6 sm:px-6 lg:px-10">
      <UpdateUserModal
        show={showRename}
        onClose={() => setShowRename(false)}
        fields={[USER_FIELD_VALUES.DISPLAY_NAME]}
        onComplete={({ displayName: newDisplayName }) => {
          setShowRename(false);
          if (newDisplayName) {
            navigate(`/user/${newDisplayName}?tab=preferences`);
          }
        }}
      />

      <BkPanel className="space-y-4">
        <h2 className="text-lg font-bold uppercase tracking-wider text-bk-gold">
          Preferences
        </h2>

        <div className="flex items-center justify-between gap-4 border-b border-bk-border pb-4">
          <div>
            <div className="font-semibold text-bk-text">Display name</div>
            <p className="text-sm text-bk-muted">
              {String(userData?.displayName || '—')}
            </p>
          </div>
          <button
            type="button"
            className="inline-flex items-center gap-2 text-bk-gold hover:text-bk-gold-bright"
            onClick={() => {
              if (nextUpdateNameAt !== undefined && nextUpdateNameAt >= Date.now()) {
                addSnackBar({
                  title: 'Locked',
                  message: `You can rename again on ${new Date(nextUpdateNameAt).toLocaleDateString()}.`,
                  variant: 'warning',
                  timeout: 2500,
                });
                return;
              }
              setShowRename(true);
            }}
          >
            <FiEdit />
            <span className="text-xs font-bold uppercase tracking-wider">Edit</span>
          </button>
        </div>

        <label className="flex cursor-pointer items-center justify-between gap-4">
          <div>
            <div className="font-semibold text-bk-text">Show piece ranks</div>
            <p className="text-sm text-bk-muted">
              Display each revealed piece&apos;s rank number on the board.
            </p>
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={displayPieceValues}
            onClick={() => void setDisplayPieceValues(!displayPieceValues)}
            className={[
              'relative h-7 w-12 shrink-0 border transition-colors',
              displayPieceValues
                ? 'border-bk-gold bg-bk-gold/30'
                : 'border-bk-border bg-bk-bg',
            ].join(' ')}
          >
            <span
              className={[
                'absolute top-0.5 h-5 w-5 bg-bk-gold-bright transition-transform',
                displayPieceValues ? 'left-6' : 'left-0.5',
              ].join(' ')}
            />
          </button>
        </label>
      </BkPanel>

      <BkPanel className="space-y-3">
        <h2 className="text-lg font-bold uppercase tracking-wider text-bk-gold">
          Account
        </h2>
        {deletionRequestedAt ? (
          <p className="text-sm text-bk-muted">
            Deletion requested on{' '}
            {new Date(deletionRequestedAt).toLocaleDateString()}. We will close
            the account and delete or anonymize personal data we no longer need.
            Purchase records may be kept where the law requires.
          </p>
        ) : (
          <>
            <p className="text-sm text-bk-muted">
              Request that this account be closed. This flags the account for
              deletion; it is not instant. Email support if you need to cancel
              the request.
            </p>
            <button
              type="button"
              disabled={deletionBusy}
              className="border border-bk-danger px-3 py-2 text-xs font-bold uppercase tracking-wider text-bk-danger hover:bg-bk-danger/10 disabled:opacity-60"
              onClick={() => {
                if (
                  !window.confirm(
                    'Request account deletion? This cannot be undone from the site.'
                  )
                ) {
                  return;
                }
                setDeletionBusy(true);
                void api
                  .requestAccountDeletion()
                  .then(async () => {
                    await queryUserData();
                    addSnackBar({
                      title: 'Requested',
                      message:
                        'Your deletion request was recorded. We will process it.',
                      variant: 'success',
                      timeout: 3000,
                    });
                  })
                  .catch((err: unknown) => {
                    addSnackBar({
                      title: 'ERROR',
                      message:
                        err instanceof Error ? err.message : String(err),
                      variant: 'danger',
                      timeout: 3000,
                    });
                  })
                  .finally(() => setDeletionBusy(false));
              }}
            >
              {deletionBusy ? 'Please wait…' : 'Request account deletion'}
            </button>
          </>
        )}
      </BkPanel>
    </div>
  );
}
